import { emptyPatientChampion, PatientChampion } from '../patient/components/models/patient-champion';
import { PatientEncounter } from '../patient/components/models/patient-encounter';
import { PatientViewModel } from '../patient/components/models/patient-view-model';
import { PayorListEntry } from '../shared/model/payor-list-entry';
import { UserPaymentMethod, Program } from '@finpay-payment-v3/payment-types';
import { PayorWithUserStatus } from './interfaces/payment-hub-interfaces';
import { GuarantorDetails } from './components/guarantor-address-fields';
import { AddressDetails, UpdatePayorInfoParams } from './services/champion-service';
import { PatientNotes, emptyPatientNote } from '../patient/components/models/patient-note';
import { createPatientNote } from '../patient/state/patient-thunk';
import { AppDispatch } from '../shared/state/store';
import { commonFields } from '../shared/validation/schemas';
import * as Yup from 'yup';
import { patientService } from '../patient/services/patient-service';
import { userService } from '../admin/services/admin-user-service';
import { ApiBatchLimits } from '../shared/enums';
import { parseAmount, formatCurrencyInput, stripCurrencyInput } from '../shared/calculators';
import { Utils } from '../shared/utils';
import { deactivatePaymentMethod, fetchUserPaymentMethods } from './services/payment-method-service';
import { CHANNEL_CODE_TO_ID } from './state/payment-context-slice';
import { UserRolePage } from '../security/model/user-role-page';
import { RolePageNames } from '../security/model/role-page-names';
import { checkPagePermission } from '../security/components/access-control';
export { parseAmount, formatCurrencyInput, stripCurrencyInput };

export function getSuccessRoute(
  paymentChannelId: number | null,
  patientId: string,
  encounterId: string,
  userRolePages: UserRolePage[]
): string {
  if (paymentChannelId === CHANNEL_CODE_TO_ID['crm']) {
    const hasEstimatesAccess = checkPagePermission({
      userRolePages,
      rolePageName: RolePageNames.Estimates,
      permitReadOnly: false,
    });
    if (!hasEstimatesAccess) {
      return `/specialist/dashboard/${patientId}?encounter=${encounterId}`;
    }
    return `/patients/${patientId}`;
  }
  if (paymentChannelId === CHANNEL_CODE_TO_ID['vt']) {
    return '/specialist/dashboard';
  }
  return `/hub/patient/${patientId}/ioc/${encounterId}`;
}

export interface ResolvePayorUserParams {
  email: string;
  clientId: number;
  patientEncounterId: number;
  firstName: string;
  lastName: string;
}

export interface ResolvePayorUserResult {
  userId: number | undefined;
  existingPayorUserId: number | undefined;
}

/**
 * Looks up a user by email.
 * - If no user exists: creates one, returns userId (to associate with payor).
 * - If user already exists: returns existingPayorUserId (for payment method creation only, NOT for payor association).
 */
export const resolvePayorUser = async (params: ResolvePayorUserParams): Promise<ResolvePayorUserResult> => {
  const { email, clientId, patientEncounterId, firstName, lastName } = params;

  const lookupResponse = await userService.getAllUsers(0, ApiBatchLimits.users, { userName: email });
  const existingUser = lookupResponse?.entity?.[0];

  if (existingUser?.userId && existingUser.isPatient) {
    console.log('[PaymentHub] Existing user found, not associating to payor. existingPayorUserId:', existingUser.userId);
    return { userId: undefined, existingPayorUserId: existingUser.userId };
  }

  const userResponse = await userService.handleCreateOrUpdateUser({
    userEmail: email,
    clientId,
    patientEncounterId,
    name: { firstName, lastName },
    filteredUsers: lookupResponse?.entity,
  });

  if (userResponse?.userId) {
    console.log('[PaymentHub] New user created:', userResponse.userId);
    return { userId: userResponse.userId, existingPayorUserId: undefined };
  }

  return { userId: undefined, existingPayorUserId: undefined };
};

export const resolveGuarantorForEnrollment = async (params: {
  email: string | undefined;
  existingUserId: number | undefined;
  firstName: string;
  lastName: string;
  clientId: number;
  patientEncounterId: number;
}): Promise<{ email: string | undefined; userId: number | undefined; paymentUserId: number | undefined; shouldLinkUser: boolean }> => {
  const { email, existingUserId, firstName, lastName, clientId, patientEncounterId } = params;
  if (existingUserId) return { email, userId: existingUserId, paymentUserId: existingUserId, shouldLinkUser: true };
  if (!email) return { email, userId: undefined, paymentUserId: undefined, shouldLinkUser: false };

  try {
    const lookupResponse = await userService.getAllUsers(0, ApiBatchLimits.users, { userName: email });
    const existingUser = lookupResponse?.entity?.[0];

    if (existingUser?.userId && existingUser.isPatient) {
      console.log('[PaymentHub] Existing user found for enrollment, not associating. paymentUserId:', existingUser.userId);
      return { email, userId: undefined, paymentUserId: existingUser.userId, shouldLinkUser: false };
    }

    const userResponse = await userService.handleCreateOrUpdateUser({
      userEmail: email,
      clientId,
      patientEncounterId,
      name: { firstName, lastName },
      filteredUsers: lookupResponse?.entity,
    });
    if (userResponse?.userId) {
      console.log('[PaymentHub] New user created for enrollment:', userResponse.userId);
      return { email, userId: userResponse.userId, paymentUserId: userResponse.userId, shouldLinkUser: true };
    }
  } catch (error) {
    console.error('[PaymentHub] Failed to find/create user for enrollment:', error);
  }
  return { email, userId: undefined, paymentUserId: undefined, shouldLinkUser: false };
};

// ============================================================================
// Payment Status Utilities
// ============================================================================

/**
 * Calculate if payment is paid in full based on pending balance.
 * A program is paid in full when pfrPendingBalance === 0.
 * 
 * This should be checked AFTER a payment has been processed to determine
 * if the payment completed the full balance.
 * 
 * @param program - The payment program (can be V3 Program type or legacy patientPaymentProgram)
 * @returns true if pfrPendingBalance is 0 (paid in full)
 */
export const isPaidInFull = (program?: Program | any): boolean => {
  if (!program?.patientPaymentSchedule) return false;
  
  const pendingBalance = program.patientPaymentSchedule.pfrPendingBalance 
  
  return pendingBalance === 0;
};


// ============================================================================
// Generic Payment Note Creation
// ============================================================================

type PaymentNoteType = 'card' | 'ach' | 'cash' | 'check' | 'facility';

interface CreatePaymentNoteParams {
  patientId: number;
  patientEncounterId: number;
  amount: number;
  paymentType: PaymentNoteType;
  last4?: string;
  showNotify?: boolean;
}

/**
 * Create a note for any payment type.
 * Called after every successful payment to maintain a record in notes.
 */
export const createPaymentNote = async (
  dispatch: AppDispatch,
  params: CreatePaymentNoteParams
): Promise<PatientNotes | undefined> => {
  const { patientId, patientEncounterId, amount, paymentType, last4, showNotify = false } = params;

  if (!patientId || !patientEncounterId) {
    console.warn('[createPaymentNote] Invalid params:', { patientId, patientEncounterId });
    return undefined;
  }

  let noteText = '';
  switch (paymentType) {
    case 'card':
      noteText = `Payment of $${amount.toFixed(2)} received via Credit Card${last4 ? ` ending in ${last4}` : ''}`;
      break;
    case 'ach':
      noteText = `Payment of $${amount.toFixed(2)} received via ACH${last4 ? ` ending in ${last4}` : ''}`;
      break;
    case 'cash':
      noteText = `Payment of $${amount.toFixed(2)} received via Cash`;
      break;
    case 'check':
      noteText = `Payment of $${amount.toFixed(2)} received via Check`;
      break;
    case 'facility':
      noteText = `$${amount.toFixed(2)} to be taken at facility`;
      break;
    default:
      noteText = `Payment of $${amount.toFixed(2)} received`;
  }

  const currentDate = new Date();

  const comment: PatientNotes = {
    ...emptyPatientNote,
    patientId,
    patientEncounterId,
    noteText,
    noteDt: currentDate.toISOString(),
    completionDueDt: '',
    isReminder: false,
  };

  try {
    const result = await dispatch(createPatientNote({
      message: 'Payment note created',
      comment,
      showNotify,
    })).unwrap();

    console.log('[createPaymentNote] Note created successfully:', result);
    return result;
  } catch (error) {
    console.error('[createPaymentNote] Failed to create note:', error);
    return undefined;
  }
};

// ============================================================================
// CRM Integration Utilities
// ============================================================================

interface ClientCrmInfo {
  isIntEnabled?: boolean;
  crmType?: {
    crmTypeSlug?: string;
    crmName?: string;
  };
}

interface TriggerCrmUpdateParams {
  patientId: number;
  patientEncounterId: number;
  client?: {
    clientId?: number;
    clientName?: string;
    clientCrm?: ClientCrmInfo[];
  };
}

/**
 * Trigger CRM integration update for a payment.
 * This should be called after any successful payment to sync with the client's CRM.
 * 
 * @returns true if CRM update was triggered, false otherwise
 */
export const triggerCrmUpdate = async (params: TriggerCrmUpdateParams): Promise<boolean> => {
  const { patientId, patientEncounterId, client } = params;
  
  const clientCrm = client?.clientCrm;
  const isCrmEnabled = clientCrm?.some((crm) => crm.isIntEnabled);
  
  console.log('[triggerCrmUpdate] CRM Status:', {
    clientId: client?.clientId,
    clientName: client?.clientName,
    hasClientCrm: !!clientCrm,
    clientCrmCount: clientCrm?.length || 0,
    isCrmEnabled,
  });
  
  if (!isCrmEnabled || !clientCrm) {
    console.log('[triggerCrmUpdate] CRM not enabled for this client, skipping');
    return false;
  }
  
  const enabledCrm = clientCrm.find((crm) => crm.isIntEnabled);
  const crmSlug = enabledCrm?.crmType?.crmTypeSlug;
  if (!crmSlug) {
    console.log('[triggerCrmUpdate] No CRM slug found, skipping');
    return false;
  }
  
  try {
    console.log('[triggerCrmUpdate] Triggering CRM update:', { patientId, patientEncounterId, crmSlug });
    await patientService.integrationUpdate({
      patientEncounterId,
      patientId,
      crmTypeSlug: crmSlug,
    });
    console.log('[triggerCrmUpdate] CRM update triggered successfully');
    return true;
  } catch (error) {
    console.error('[triggerCrmUpdate] CRM integration failed:', error);
    return false;
  }
};

export const stripeInputStyles = {
  width: '100%',
  height: '44.16px',
  padding: '12px',
  fontSize: '16px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  color: '#30313d',
  backgroundColor: '#fff',
  border: '1px solid #e6e6e6',
  borderRadius: '5px',
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

/**
 * Shared label styles for payment form components
 */
export const stripeLabelStyles = {
  display: 'block',
  fontSize: '14.88px',
  fontWeight: 400,
  color: '#30313d',
  mb: '4px',
  lineHeight: '17.856px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
};

/**
 * Shared select styles for payment form components (matches Stripe styling)
 */
export const stripeSelectStyles: React.CSSProperties = {
  width: '100%',
  height: '44.16px',
  padding: '12px',
  fontSize: '16px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  color: '#30313d',
  backgroundColor: '#fff',
  border: '1px solid #e6e6e6',
  borderRadius: '5px',
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 8px center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '20px',
  paddingRight: '32px',
};

export const NEW_PAYOR_ID = 'NEW_PAYOR';

export const PAYMENT_SOURCE_TYPES = {
  CARD: 'Card',
  ACH: 'ACH',
  CASH: 'Cash',
  CHECK: 'Check',
} as const;

type PaymentSourceType = keyof typeof PAYMENT_SOURCE_TYPES;

export interface ResolveRecurringMethodParams {
  sectionRef: {
    getSelectedMethodId: () => number | null;
    isAddingNew: () => boolean;
    createAndSavePaymentMethod: (params: { userId: number; authToken: string | null | undefined; stripeCustomerId?: string }) => Promise<{ success: boolean; paymentMethodId: number | null; error?: string }>;
  };
  userId: number | undefined;
  authToken: string | null | undefined;
  stripeCustomerId?: string;
}

export interface ResolveRecurringMethodResult {
  success: boolean;
  recurringMethodId: number | null;
  error?: string;
}

/**
 * Resolves the recurring payment method: returns the existing selection
 * or creates a new one if the user chose "Add new".
 * 
 * Used by all enrollment paths (zero-down, card/ACH, cash/check).
 */
export const resolveRecurringPaymentMethod = async (
  params: ResolveRecurringMethodParams
): Promise<ResolveRecurringMethodResult> => {
  const { sectionRef, userId, authToken, stripeCustomerId } = params;

  const selectedId = sectionRef.getSelectedMethodId();
  if (!sectionRef.isAddingNew()) {
    return { success: true, recurringMethodId: selectedId };
  }

  if (!userId) {
    return { success: false, recurringMethodId: null, error: 'Unable to save payment method: user account could not be resolved.' };
  }

  try {
    const result = await sectionRef.createAndSavePaymentMethod({
      userId,
      authToken,
      stripeCustomerId,
    });

    if (result.success && result.paymentMethodId) {
      console.log('[PaymentHub] Recurring payment method saved:', result.paymentMethodId);
      return { success: true, recurringMethodId: result.paymentMethodId };
    }

    return { success: false, recurringMethodId: null, error: result.error || 'Failed to save recurring payment method.' };
  } catch (err: any) {
    console.error('[PaymentHub] Error creating recurring payment method:', err);
    return { success: false, recurringMethodId: null, error: err?.message || 'Unknown error saving payment method' };
  }
};

export const getDateInFuture = (daysInFuture: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() + daysInFuture);
  return date;
};


export const getValidRecurringDate = (): Date => {
  const date = getDateInFuture(30);
  const dayOfMonth = date.getDate();
  if (dayOfMonth >= 29) {
    date.setMonth(date.getMonth() + 1);
    date.setDate(1);
  }
  return date;
};

// ============================================================================
// Payment Method Formatting
// ============================================================================

/**
 * Format a UserPaymentMethod for display (e.g., "Visa •••• 4242")
 */
export const formatPaymentMethodDisplay = (method: UserPaymentMethod): string => {
  const isCard = method.paymentMethodType?.paymentMethodName?.toLowerCase() === 'card';
  
  if (isCard) {
    const brand = method.cardBrand 
      ? method.cardBrand.charAt(0).toUpperCase() + method.cardBrand.slice(1) 
      : 'Card';
    return `${brand} •••• ${method.last4 || ''}`;
  } else {
    return `Bank •••• ${method.last4 || ''}`;
  }
};

/**
 * Format card expiration date (e.g., "01/2025")
 * Returns null for non-card payment methods
 */
export const formatExpiration = (method: UserPaymentMethod): string | null => {
  const isCard = method.paymentMethodType?.paymentMethodName?.toLowerCase() === 'card';
  
  if (isCard && method.expMonth && method.expYear) {
    return `${method.expMonth.toString().padStart(2, '0')}/${method.expYear}`;
  }
  return null;
};

// ============================================================================
// Payment Method Filtering
// ============================================================================

export interface SavedBankAccount {
  userPaymentMethodId: number;
  last4: string;
  bankName?: string;
  isDefault?: boolean;
}

/**
 * Filter bank accounts from a list of payment methods
 * Only returns active bank accounts
 * BankAccount typeId = 2 (from PaymentMethod enum)
 * 
 * Note: API may return paymentMethodTypeId directly on the object (not in nested paymentMethodType)
 */
export const filterBankAccounts = (paymentMethods?: UserPaymentMethod[]): SavedBankAccount[] => {
  if (!paymentMethods) return [];
  return paymentMethods
    .filter(pm => {
      const typeName = pm.paymentMethodType?.paymentMethodName?.toLowerCase();
      // Get typeId from nested object or directly from pm (API inconsistency)
      const typeId = pm.paymentMethodType?.paymentMethodTypeId ?? (pm as any).paymentMethodTypeId;
      // Check by name OR by typeId (2 = BankAccount from PaymentMethod enum)
      const isBank = typeName === 'bank_account' || typeName === 'bank' || typeName === 'ach' || typeId === 2;
      return isBank && pm.isActive !== false;
    })
    .map(pm => ({
      userPaymentMethodId: pm.userPaymentMethodId,
      last4: pm.last4 || '****',
      bankName: pm.cardBrand || 'Bank Account',
      isDefault: false,
    }));
};

/**
 * Filter card payment methods from a list of payment methods
 * Only returns active cards
 * Checks both paymentMethodType.paymentMethodName and paymentMethodTypeId
 * Card typeId = 1 (from PaymentMethod enum)
 * 
 * Note: API may return paymentMethodTypeId directly on the object (not in nested paymentMethodType)
 */
export const filterCardMethods = (paymentMethods?: UserPaymentMethod[]): UserPaymentMethod[] => {
  if (!paymentMethods) return [];
  return paymentMethods.filter(pm => {
    const typeName = pm.paymentMethodType?.paymentMethodName?.toLowerCase();
    // Get typeId from nested object or directly from pm (API inconsistency)
    const typeId = pm.paymentMethodType?.paymentMethodTypeId ?? (pm as any).paymentMethodTypeId;
    // Check by name OR by typeId (1 = Card from PaymentMethod enum)
    const isCard = typeName === 'card' || typeId === 1;
    const isActive = pm.isActive !== false;
    return isCard && isActive;
  });
};

interface DefaultCardMethodResult {
  defaultCardMethodId: string | null;
  stripeCustomerId: string | null;
}

/**
 * Gets the default card method ID and Stripe customer ID from a list of payment methods.
 */
export const getDefaultCardMethodAndCustomerId = (paymentMethods?: UserPaymentMethod[]): DefaultCardMethodResult => {
  if (!paymentMethods || paymentMethods.length === 0) {
    return { defaultCardMethodId: null, stripeCustomerId: null };
  }

  const methodWithCustomerId = paymentMethods.find((m: any) => m.extPaymentUserId);
  const stripeCustomerId = (methodWithCustomerId as any)?.extPaymentUserId || null;

  const cardMethods = filterCardMethods(paymentMethods);
  const firstCardMethod = cardMethods[0];
  const defaultCardMethodId = firstCardMethod?.userPaymentMethodId?.toString() || null;

  return { defaultCardMethodId, stripeCustomerId };
};

// ============================================================================
// Payor List Utilities
// ============================================================================

/**
 * Gets a list of patients and champions that can be used as payors.
 * This is a hub-specific copy of the function from Utils.
 * Fetches payment methods for each user with a valid userId.
 * 
 * @param selectedPatient - The selected patient
 * @param selectedEncounter - The selected patient encounter
 * @param guarantorChampsOnly - If true, only include champions that are guarantors
 * @param authToken - Optional authentication token for API calls
 * @returns Promise of array of payor list entries with payment methods
 */
export async function getPatientsAndChampionsList(
  selectedPatient: PatientViewModel,
  selectedEncounter: PatientEncounter,
  guarantorChampsOnly: boolean,
  authToken?: string
): Promise<PayorListEntry[]> {
  const patientsAndChampionsList: PayorListEntry[] = [];
  
  const fetchPaymentMethods = async (userId: number): Promise<UserPaymentMethod[]> => {
    if (!userId || userId <= 0 || !authToken) {
      return [];
    }
    const result = await fetchUserPaymentMethods({ userId, authToken });
    return result.success ? result.paymentMethods : [];
  };
  
  // Check if any champion is the guarantor
  const hasGuarantorChampion = selectedEncounter?.patientChampion?.some(
    (champion: PatientChampion) => champion?.isGuarantor === true
  ) || false;

  // Only add patient if they have valid data (patientId must be > 0)
  if (selectedPatient?.patientId && selectedPatient.patientId > 0 && selectedPatient?.contact) {
    const firstName = selectedPatient.contact.firstName || '';
    const lastName = selectedPatient.contact.lastName || '';
    const streetAddress1 = selectedPatient.contact.primaryAddress?.streetAddress1 || '';
    const streetAddress2 = selectedPatient.contact.primaryAddress?.streetAddress2 || '';
    const city = selectedPatient.contact.primaryAddress?.city || '';
    const state = selectedPatient.contact.primaryAddress?.state;
    const stateString = typeof state === 'string' ? state : (state as any)?.stateCode || (state as any)?.stateName || '';
    const zipCode = selectedPatient.contact.primaryAddress?.zipCode || '';
    
    const addressParts = [
      streetAddress1,
      streetAddress2,
      city,
      stateString,
      zipCode
    ].filter(part => part && typeof part === 'string' && part.trim() !== '');
    
    const patientUserId = selectedPatient.userId ?? 0;
    const paymentMethods = patientUserId > 0 ? await fetchPaymentMethods(patientUserId) : [];
    
    // Patient is only a guarantor if no champion is marked as guarantor
    const isPatientGuarantor = !hasGuarantorChampion;
    
    patientsAndChampionsList.push({
      id: selectedPatient.patientId,
      contactId: selectedPatient.contact.contactId,
      userId: patientUserId,
      name: `${firstName} ${lastName}`.trim() || 'Unknown',
      email: selectedPatient.contact.email,
      isGuarantor: isPatientGuarantor,
      address: addressParts.join(', ') || undefined,
      paymentMethods: paymentMethods.length > 0 ? paymentMethods : undefined,
      type: 'patient' as const,
    });
  }

  // Process champions
  if (selectedEncounter?.patientChampion?.length > 0 && 
      selectedEncounter?.patientChampion[0] !== emptyPatientChampion) {
    for (const champion of selectedEncounter.patientChampion) {
      if (guarantorChampsOnly && !champion?.isGuarantor) {
        continue;
      }

      const championUserId = champion?.userId ?? 0;
      const paymentMethods = championUserId > 0 ? await fetchPaymentMethods(championUserId) : [];
      
      const championState = champion?.contact?.primaryAddress?.state;
      const championStateString = typeof championState === 'string' 
        ? championState 
        : (championState as any)?.stateCode || (championState as any)?.stateName || '';
      
      patientsAndChampionsList.push({
        id: champion?.patientChampionId,
        contactId: champion?.contact?.contactId,
        userId: championUserId,
        name: `${champion?.contact?.firstName} ${champion?.contact?.lastName}`,
        email: champion?.contact.email,
        isGuarantor: champion?.isGuarantor,
        address: `${champion?.contact?.primaryAddress?.streetAddress1 || ''} ${champion?.contact?.primaryAddress?.streetAddress2 || ''} ${champion?.contact?.primaryAddress?.city || ''}, ${championStateString} ${champion?.contact?.primaryAddress?.zipCode || ''}`.trim() || undefined,
        paymentMethods: paymentMethods.length > 0 ? paymentMethods : undefined,
        type: 'champion' as const,
      });
    }
  }
  
  return patientsAndChampionsList;
}

// ============================================================================
// Payment Hub Form Validation
// ============================================================================

/**
 * Create a Yup validation schema for the Payment Hub form.
 * Handles comma-formatted amount strings with different minimum amounts
 * based on payment source type.
 *
 * Cash/Check: Minimum 10 cents (can be $0 for facility-only payments)
 * Card/ACH: Minimum 50 cents
 *
 * When payAtFacility is checked:
 * - amount (down payment) is NOT required
 * - payAtFacilityAmount IS required
 *
 * When isEnrollmentOnly is true (zero down payment or down payment fully paid):
 * - amount is NOT required (can be $0)
 *
 * @param paymentSource - The payment source type (CARD, ACH, CASH, CHECK)
 * @param isEnrollmentOnly - Whether this is an enrollment-only scenario (no payment required)
 * @returns Yup validation schema
 */
export const createPaymentHubValidationSchema = (paymentSource: keyof typeof PAYMENT_SOURCE_TYPES, isEnrollmentOnly: boolean = false) => {
  const isCashOrCheck = paymentSource === 'CASH' || paymentSource === 'CHECK';
  const minAmount = isCashOrCheck ? 0.1 : 0.5;
  const minAmountMessage = isCashOrCheck ? 'Minimum payment is $0.10' : 'Minimum payment is $0.50';

  return Yup.object({
    payorName: isEnrollmentOnly ? Yup.string().optional() : Yup.string().required('Please select a payor'),
    receiptEmail: isEnrollmentOnly ? commonFields.EMAIL.optional() : commonFields.EMAIL.required('Portal Login Email is a required field'),
    amount: Yup.string()
      .test('is-valid-amount', 'Amount must be a valid number', (value) => {
        if (!value || value === '') return true;
        const numValue = parseAmount(value);
        return !isNaN(numValue) && numValue >= 0;
      })
      .test('min-amount', minAmountMessage, function(value) {
        const { payAtFacility } = this.parent;
        // If enrollment-only, amount is not required (can be empty or zero)
        if (isEnrollmentOnly) return true;
        // If payAtFacility is checked, amount is not required (can be empty or zero)
        if (payAtFacility) return true;
        // If no value entered, show minimum amount error
        if (!value || value === '') return false;
        const numValue = parseAmount(value);
        // Must meet minimum amount
        return numValue >= minAmount;
      }),
    payAtFacility: Yup.boolean(),
    payAtFacilityAmount: Yup.string()
      .test('is-valid-facility-amount', 'Amount must be a valid number', (value) => {
        if (!value || value === '') return true;
        const numValue = parseAmount(value);
        return !isNaN(numValue) && numValue >= 0;
      })
      .test('required-when-facility-checked', 'Please enter an amount to be taken at facility', function(value) {
        const { payAtFacility } = this.parent;
        if (!payAtFacility) return true; // Not required if checkbox is unchecked
        if (!value || value === '') return false; // Required if checkbox is checked
        const numValue = parseAmount(value);
        return numValue > 0; // No minimum amount for facility payment
      }),
    paymentReason: Yup.string().max(100, 'Maximum payment reason is 100 characters').optional(),
    paymentSources: Yup.string().when('useExistingSource', {
      is: true,
      then: Yup.string().test(
        'custom-payment-sources-validation',
        'Required field',
        (v) => !!v && v !== '-1'
      ),
    }),
  });
};

export const getGuarantorAddressDetails = (
  payor: PayorWithUserStatus,
  selectedEncounter: PatientEncounter | null,
  selectedPatient: PatientViewModel | null
): GuarantorDetails | null => {
  if (!payor.isGuarantor) return null;

  if (payor.type === 'champion') {
    const champion = selectedEncounter?.patientChampion?.find(
      c => c.patientChampionId === payor.id
    );
    if (champion) {
      return {
        streetAddress1: champion.contact?.primaryAddress?.streetAddress1 || '',
        city: champion.contact?.primaryAddress?.city || '',
        state: champion.contact?.primaryAddress?.state?.stateCode || '',
        zipCode: champion.contact?.primaryAddress?.zipCode || '',
        phone: Utils.formatPhoneNumber(champion.contact?.primPhoneNum || ''),
        birthDate: champion.contact?.birthDate || '',
        relationshipToPatient: champion.relationshipToPatient || '',
      };
    }
  } else if (payor.type === 'patient') {
    return {
      streetAddress1: selectedPatient?.contact?.primaryAddress?.streetAddress1 || '',
      city: selectedPatient?.contact?.primaryAddress?.city || '',
      state: selectedPatient?.contact?.primaryAddress?.state?.stateCode || '',
      zipCode: selectedPatient?.contact?.primaryAddress?.zipCode || '',
      phone: Utils.formatPhoneNumber(selectedPatient?.contact?.primPhoneNum || ''),
      birthDate: selectedPatient?.contact?.birthDate || '',
      relationshipToPatient: 'Self',
    };
  }
  return null;
};

/**
 * Resolves which guarantor form is visible and builds the updatePayorInfo params.
 * Shared across all flows (enrollment and non-enrollment) to ensure contact info
 * is persisted consistently.
 */
export const buildUpdatePayorInfoParams = (params: {
  isZeroDownPayment: boolean;
  isDownPaymentFullyPaid: boolean;
  isProgramSetup: boolean;
  useForRecurring?: boolean;
  isFullPayProgram?: boolean;
  isDownPaymentGuarantorCheckboxChecked: boolean;
  isRecurringGuarantorCheckboxChecked: boolean;
  payorsWithStatus: PayorWithUserStatus[];
  downPaymentPayorId?: string;
  downPaymentEmail?: string;
  downPaymentAddressDetails?: AddressDetails;
  recurringPayorId?: string;
  recurringEmail?: string;
  recurringAddressDetails?: AddressDetails;
  patientId?: string;
}): UpdatePayorInfoParams => {
  const {
    isZeroDownPayment,
    isDownPaymentFullyPaid,
    isProgramSetup,
    useForRecurring,
    isFullPayProgram,
    isDownPaymentGuarantorCheckboxChecked,
    isRecurringGuarantorCheckboxChecked,
    payorsWithStatus,
    downPaymentPayorId,
    downPaymentEmail,
    downPaymentAddressDetails,
    recurringPayorId,
    recurringEmail,
    recurringAddressDetails,
    patientId,
  } = params;

  const selectedPayor = downPaymentPayorId
    ? payorsWithStatus.find(p => p.id?.toString() === downPaymentPayorId)
    : undefined;
  const selectedRecurringPayor = recurringPayorId
    ? payorsWithStatus.find(p => p.id?.toString() === recurringPayorId)
    : undefined;

  const showDownPaymentGuarantorForm = !isZeroDownPayment && !isDownPaymentFullyPaid &&
    ((isProgramSetup && (useForRecurring || isFullPayProgram)) || !isProgramSetup) &&
    !isRecurringGuarantorCheckboxChecked &&
    (isDownPaymentGuarantorCheckboxChecked || !!selectedPayor?.isGuarantor);

  const showRecurringGuarantorForm = !isDownPaymentGuarantorCheckboxChecked &&
    (isRecurringGuarantorCheckboxChecked || !!selectedRecurringPayor?.isGuarantor);

  const guarantorFormPayorId = showDownPaymentGuarantorForm
    ? downPaymentPayorId
    : (showRecurringGuarantorForm ? recurringPayorId : undefined);
  const guarantorFormAddressDetails = showDownPaymentGuarantorForm
    ? downPaymentAddressDetails
    : (showRecurringGuarantorForm ? recurringAddressDetails : undefined);

  return {
    newGuarantorPayorId: isDownPaymentGuarantorCheckboxChecked
      ? downPaymentPayorId
      : (isRecurringGuarantorCheckboxChecked ? recurringPayorId : undefined),
    guarantorFormPayorId,
    guarantorFormAddressDetails,
    payorsWithStatus,
    patientId,
    downPaymentPayorId,
    downPaymentEmail,
    recurringPayorId,
    recurringEmail,
  };
};

export const deleteAndRefetchPaymentMethods = async (params: {
  userId: number;
  userPaymentMethodId: number;
  authToken?: string | null;
}): Promise<{ success: boolean; message: string; paymentMethods: UserPaymentMethod[] }> => {
  const { userId, userPaymentMethodId, authToken } = params;

  const result = await deactivatePaymentMethod({ userId, userPaymentMethodId, authToken });
  if (!result.success) {
    return { success: false, message: result.message, paymentMethods: [] };
  }

  const refetchResult = await fetchUserPaymentMethods({ userId, authToken });
  return {
    success: true,
    message: result.message,
    paymentMethods: refetchResult.success ? refetchResult.paymentMethods : [],
  };
};
